'use client'
import { trackEvent } from '@a24/analytics'
import { getVivenuContext } from '@a24/ui/VivenuContext'
import Script from 'next/script'
import { useEffect } from 'react'

type VivenuAppliedDiscountItem = {
  cartItemId: string
  regularPrice: number
  price: number
}

type VivenuTicket = {
  _id: string
  price: number
}

type VivenuCheckoutItem = {
  eventId: string
  regularPrice: number
  tickets: VivenuTicket[]
  appliedDiscountInfo: {
    items: VivenuAppliedDiscountItem[]
  }
}

type VivenuCheckout = {
  _id: string
  currency: string
  value: number
  items: VivenuCheckoutItem[]
}

const Vivenu = () => {
  // NB: Intercept redirect messages from the vivenu iframe BEFORE embed.js
  // processes them. This prevents the page from navigating away when
  // the user gets put into a queue or completes checkout.
  // Must be registered before embed.js init() adds its own listener.
  useEffect(() => {
    const VIVENU_ORIGINS = ['https://vivenu.com', 'https://vivenu.dev']
    const handleMessage = (e: MessageEvent) => {
      if (!VIVENU_ORIGINS.includes(e.origin)) return
      if (e.data?.type === 'redirect' && e.data?.url) {
        e.stopImmediatePropagation()
        const iframe = document.querySelector(
          'iframe[src*="vivenu"]'
        ) as HTMLIFrameElement
        if (iframe) {
          iframe.src = e.data.url
        }
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  return (
    <Script
      type='text/javascript'
      src='https://vivenu.dev/web/deliver/js/v1/embed.js'
      strategy='afterInteractive'
      onLoad={() => {
        // @ts-ignore - VIVENU is injected globally by embed.js
        window.VIVENU = window.VIVENU || {}
        // @ts-ignore
        window.VIVENU.onCheckoutCompleted = (checkout: VivenuCheckout) => {
          /*
           * Checkout object doesn't have all data needed for our
           * event tracking, so context is set when the modal is opened
           * for use here
           */
          const ctx = getVivenuContext()

          /*
           * Checkout items are per-show, not per-ticket.
           * Since we only support single-event checkout, the first
           * item will contain all tickets
           */
          const item = checkout.items[0]
          if (!item) return

          // Even if no discounts are applied, checkout item always has appliedDiscountInfo
          const creditAmount =
            -1 *
            item.appliedDiscountInfo.items.reduce(
              (sum, d) => sum + (d.regularPrice - d.price),
              0
            )

          /* Since the modal only opens on desktop, these events
           * are only tracked on desktop. On mobile, Vivenu
           * redirects to their site for checkout, so we lose
           * the events.
           */

          trackEvent({
            name: 'checkout_completed',
            data: {
              orderId: checkout._id,
              currency: checkout.currency,
              totalAmount: checkout.value,
              regularPrice: item.regularPrice,
              creditAmount,
              vivenuEventId: ctx.vivenuEventId,
              vivenuPerformanceId: item.eventId,
              showName: ctx.showName,
              startDate: ctx.startDate,
              category: ctx.category,
              tickets: item.tickets.map((ticket) => {
                const discountInfo = item.appliedDiscountInfo.items.find(
                  (d) => d.cartItemId === ticket._id
                )
                return {
                  ticketId: ticket._id,
                  price: ticket.price,
                  discount: discountInfo
                    ? discountInfo.regularPrice - discountInfo.price
                    : 0
                }
              })
            }
          })

          for (const ticket of item.tickets) {
            const discountInfo = item.appliedDiscountInfo.items.find(
              (d) => d.cartItemId === ticket._id
            )
            trackEvent({
              name: 'product_checkout',
              data: {
                orderId: checkout._id,
                currency: checkout.currency,
                price: ticket.price,
                discount: discountInfo
                  ? discountInfo.regularPrice - discountInfo.price
                  : 0,
                vivenuEventId: ctx.vivenuEventId,
                vivenuPerformanceId: item.eventId,
                showName: ctx.showName,
                startDate: ctx.startDate,
                category: ctx.category
              }
            })
          }
        }
      }}
      onError={(e) => {
        console.error('Error loading Vivenu script:', e)
      }}
    />
  )
}

export default Vivenu
